'use client';

import AlertComponent, { AlertType } from '@spa/components/alert/alert.component';
import { Form, Formik, FormikHelpers, getIn } from 'formik';
import FormGroupComponent, { InputType } from '@spa/components/form-group/form-group.component';
import PopupComponent, { type PopupComponentRef, PopupType } from '@spa/components/popup/popup.component';
import { ProductReviewFormField, ProductReviewFormStatus, ProductReviewPhotoUploadStatus, ProductReviewSubmitStatus, ProductReviewValidationError } from '@spa/generated/graphql/graphql';
import { ReactElement, useEffect, useReducer, useRef, useState } from 'react';
import { WriteReviewFormFields, initialWriteReviewFormValues } from './bazaarvoiceWriteReviewModalTypes';
import { readDeviceFingerprint, triggerDeviceFingerprintGeneration } from './bazaarvoiceDeviceFingerprint';
import BazaarvoiceReviewStarsInputComponent from './bazaarvoice-review-stars-input.component';
import BazaarvoiceReviewTermsModalComponent from './bazaarvoice-review-terms-modal.component';
import ButtonComponent from '@spa/components/button/button/button.component';
import { ButtonType } from '@spa/components/button/types/ButtonType';
import FileInputComponent from '@spa/components/fileInput/file-input.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { SubmitProductReviewPhoto } from '@spa/lib/graphql/product-reviews/submitProductReview';
import bazaarvoiceWriteReviewModalSchema from './bazaarvoiceWriteReviewModalSchema';
import { convertFileToBase64 } from '@spa/lib/convertFileToBase64';
import { createPortal } from 'react-dom';
import logger from '@spa/lib/logger';
import mapProductReviewValidationErrors from './bazaarvoiceProductReviewValidationErrors';
import styles from './bazaarvoice-write-review-modal.component.module.scss';
import useHasOpenedOnce from '@spa/lib/react/hooks/useHasOpenedOnce';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useProductReviewsAPI from '@spa/lib/react/hooks/useProductReviewsAPI';
export interface Props {
  readonly isOpen: boolean;
  readonly onClosed: () => void;
  readonly productCode: string;
  readonly onSubmitted: () => void;
}
interface WriteReviewModalStatus {
  readonly hasError: boolean;
}
const initialWriteReviewModalStatus: WriteReviewModalStatus = {
  hasError: false
};
const MAX_PHOTOS_TOTAL_SIZE_BYTES: number = 10 * 1024 * 1024;
const ACCEPTED_PHOTO_TYPES: ReadonlyArray<string> = ['image/png', 'image/jpeg', 'image/jpg', 'image/heic', 'image/tiff'];

// Bazaarvoice's custom, account-configured questions all use this id prefix, distinct from the
// standard/reserved fields (rating, reviewtext, hostedauthentication_*, ...) this form already
// has dedicated, hand-built inputs for.
const EXTRA_FIELD_ID_PREFIX = 'contextdatavalue_';
const BAZAARVOICE_FIELD_TYPE_TO_INPUT_TYPE: Record<string, InputType> = {
  SelectInput: InputType.SELECT,
  TextInput: InputType.TEXT,
  TextAreaInput: InputType.TEXTAREA,
  BooleanInput: InputType.CHECKBOX
};
const getExtraFieldInputType = (fieldType: string): InputType => BAZAARVOICE_FIELD_TYPE_TO_INPUT_TYPE[fieldType] ?? InputType.TEXT;

// Loading state and fetched fields always change together for a given productCode fetch, so
// they're modeled as one reducer instead of separate useState calls that could momentarily
// disagree (e.g. stale fields rendered while a new fetch is already marked as loading).
type ExtraFieldsState = {
  readonly status: 'loading';
  readonly fields: ReadonlyArray<ProductReviewFormField>;
} | {
  readonly status: 'loaded';
  readonly fields: ReadonlyArray<ProductReviewFormField>;
};
type ExtraFieldsAction = {
  readonly type: 'fetchStarted';
} | {
  readonly type: 'fetchSettled';
  readonly fields: ReadonlyArray<ProductReviewFormField>;
};
const initialExtraFieldsState: ExtraFieldsState = {
  status: 'loading',
  fields: []
};
const extraFieldsReducer = (state: ExtraFieldsState, action: ExtraFieldsAction): ExtraFieldsState => {
  switch (action.type) {
    case 'fetchStarted':
      return {
        status: 'loading',
        fields: state.fields
      };
    case 'fetchSettled':
      return {
        status: 'loaded',
        fields: action.fields
      };
    default:
      return state;
  }
};

// A validation-failure response can mix field names this alias table recognizes with ones it
// doesn't; only the recognized ones get an inline field error, so the generic banner also has
// to fire whenever at least one error couldn't be mapped, not only when all of them are unmapped.
const applyValidationErrors = (validationErrors: ReadonlyArray<ProductReviewValidationError>, setFieldError: (field: string, message: string) => void, setStatus: (status: WriteReviewModalStatus) => void): void => {
  const fieldErrors = mapProductReviewValidationErrors(validationErrors);
  Object.entries(fieldErrors).forEach(([field, message]) => setFieldError(field, message));
  if (Object.keys(fieldErrors).length < validationErrors.length) {
    setStatus({
      hasError: true
    });
  }
};
function BazaarvoiceWriteReviewModalComponent({
  isOpen,
  onClosed,
  productCode,
  onSubmitted
}: Readonly<Props>): ReactElement | null {
  const {
    formatMessage
  }: NamespacedIntl = useNamespacedIntl('bazaarvoice-write-review-modal');
  const {
    formatMessage: formsFormatMessage
  }: NamespacedIntl = useNamespacedIntl('forms');
  const {
    uploadProductReviewPhoto,
    submitProductReview,
    getProductReviewForm
  } = useProductReviewsAPI();
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [extraFieldsState, dispatchExtraFieldsAction] = useReducer(extraFieldsReducer, initialExtraFieldsState);
  const hasOpened = useHasOpenedOnce(isOpen);
  const popupRef = useRef<PopupComponentRef>(null);
  // Submit success and the plain "cancel/close" path both go through PopupComponent's
  // onClosed (fired after the 800ms close animation) so the form is never reset — and the
  // success modal never opens — while the write-review modal is still visually closing.
  const pendingSuccessRef = useRef(false);
  const resetFormRef = useRef<(() => void) | null>(null);
  const uploadedPhotoCacheRef = useRef<Map<File, SubmitProductReviewPhoto>>(new Map());
  const isSubmittingRef = useRef(false);
  // Bumped whenever the in-flight submission's outcome must be ignored (the modal closed, or
  // productCode moved on) so a late-resolving upload/submit can't act on a form that no longer
  // reflects what it was submitted for.
  const submissionGenerationRef = useRef(0);

  // productCode changing means the form is now for a different product: reset every piece of
  // cross-render state for it. This all has to happen post-commit rather than during render --
  // Formik's resetForm updates a different component's state, which React disallows calling
  // synchronously mid-render, and refs must not be read or written during render either.
  useEffect(() => {
    resetFormRef.current?.();
    submissionGenerationRef.current += 1;
    uploadedPhotoCacheRef.current.clear();
    pendingSuccessRef.current = false;
  }, [productCode]);

  // The device-fingerprint loader script is injected as soon as the modal opens so it has time to
  // compute a "finished" blackbox before the user submits (readDeviceFingerprint() reads it at
  // submit time). This synchronizes an external script with the modal's visibility -- isOpen is a
  // parent-controlled prop, so there is no in-component open event to hang this on.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    triggerDeviceFingerprintGeneration();
  }, [isOpen]);

  // Bazaarvoice accounts can require custom questions beyond this form's fixed fields (e.g. "where
  // did you buy this?"). Fetch them per product so submissions don't fail the account's own,
  // otherwise-invisible validation. A failed/unavailable fetch fails open -- it must not block
  // writing a review over fields Bazaarvoice's own form definition happens to be unreachable for.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    let isCancelled = false;
    dispatchExtraFieldsAction({
      type: 'fetchStarted'
    });
    getProductReviewForm(productCode).then(result => {
      if (isCancelled) {
        return;
      }
      dispatchExtraFieldsAction({
        type: 'fetchSettled',
        fields: result.status === ProductReviewFormStatus.AVAILABLE ? result.fields : []
      });
    }).catch((error: unknown) => {
      if (!isCancelled) {
        logger.error(error, 'Error loading product review form fields');
        dispatchExtraFieldsAction({
          type: 'fetchSettled',
          fields: []
        });
      }
    });
    return (): void => {
      isCancelled = true;
    };
  }, [isOpen, productCode, getProductReviewForm]);
  if (!hasOpened) {
    return null;
  }
  const reviewFormFields = extraFieldsState.fields;
  const isLoadingExtraFormFields = extraFieldsState.status === 'loading';
  const validationSchema = bazaarvoiceWriteReviewModalSchema(formatMessage, formsFormatMessage, reviewFormFields);
  const handlePopupClosed = (): void => {
    if (isSubmittingRef.current) {
      return;
    }
    submissionGenerationRef.current += 1;
    resetFormRef.current?.();
    uploadedPhotoCacheRef.current.clear();
    setIsTermsModalOpen(false);
    if (pendingSuccessRef.current) {
      pendingSuccessRef.current = false;
      onSubmitted();
    }
    onClosed();
  };
  const handleSubmit = async (values: WriteReviewFormFields, {
    setFieldError,
    setSubmitting,
    setStatus
  }: FormikHelpers<WriteReviewFormFields>): Promise<void> => {
    setStatus(initialWriteReviewModalStatus);
    const generation = submissionGenerationRef.current;
    const isCurrent = (): boolean => submissionGenerationRef.current === generation;
    // React Compiler can't yet lower a try/finally, so every exit path below calls this
    // explicitly instead of relying on a finally block.
    const finishSubmitting = (): void => {
      if (isCurrent()) {
        setSubmitting(false);
      }
    };
    try {
      const uploadedPhotos: SubmitProductReviewPhoto[] = [];
      for (const photo of values.photos) {
        const cachedPhoto = uploadedPhotoCacheRef.current.get(photo);
        if (cachedPhoto) {
          uploadedPhotos.push(cachedPhoto);
          continue;
        }
        const base64Content = await convertFileToBase64(photo);
        const content = base64Content.split(',')[1] ?? '';
        const uploadResult = await uploadProductReviewPhoto(productCode, photo.name, photo.type, content);
        if (!isCurrent()) {
          return;
        }
        if (uploadResult.status === ProductReviewPhotoUploadStatus.VALIDATION_FAILED) {
          applyValidationErrors(uploadResult.validationErrors, setFieldError, setStatus);
          finishSubmitting();
          return;
        }
        if (uploadResult.status === ProductReviewPhotoUploadStatus.UNAVAILABLE) {
          setFieldError('photos', formatMessage('errors.submitFailed'));
          finishSubmitting();
          return;
        }
        if (uploadResult.photo) {
          const uploadedPhoto: SubmitProductReviewPhoto = {
            url: uploadResult.photo.normalUrl
          };
          uploadedPhotoCacheRef.current.set(photo, uploadedPhoto);
          uploadedPhotos.push(uploadedPhoto);
        }
      }
      const extraFieldsMetadata = Object.entries(values.extraFields).filter((entry): entry is [string, string | boolean] => Boolean(entry[1])).map(([key, value]) => ({
        key,
        value: String(value)
      }));
      const deviceFingerprint = readDeviceFingerprint();
      const submitResult = await submitProductReview(productCode, values.rating, values.reviewText, values.reviewTitle, values.nickname, values.email, values.recommendation === 'yes', uploadedPhotos, extraFieldsMetadata, deviceFingerprint);
      if (!isCurrent()) {
        return;
      }
      if (submitResult.status === ProductReviewSubmitStatus.VALIDATION_FAILED) {
        applyValidationErrors(submitResult.validationErrors, setFieldError, setStatus);
        finishSubmitting();
        return;
      }
      if (submitResult.status === ProductReviewSubmitStatus.UNAVAILABLE) {
        setStatus({
          hasError: true
        });
        finishSubmitting();
        return;
      }
      pendingSuccessRef.current = true;
      popupRef.current?.close();
      finishSubmitting();
    } catch (error_0) {
      if (!isCurrent()) {
        return;
      }
      logger.error(error_0, 'Error submitting product review');
      setStatus({
        hasError: true
      });
      finishSubmitting();
    }
    if (isCurrent()) {
      setSubmitting(false);
    }
  };
  return createPortal(<PopupComponent ref={popupRef} isOpen={isOpen} onClosed={handlePopupClosed} focusDialogOnOpen={true} closable={true} popupType={PopupType.DEFAULT} title={formatMessage('headline')}>
      <Formik initialValues={initialWriteReviewFormValues} initialStatus={initialWriteReviewModalStatus} validationSchema={validationSchema} validateOnBlur={true} onSubmit={handleSubmit}>
        {({
        values: values_0,
        errors,
        touched,
        isSubmitting,
        status,
        submitCount,
        setFieldValue,
        setFieldTouched,
        resetForm
      }) => {
        resetFormRef.current = (): void => resetForm({
          values: initialWriteReviewFormValues
        });
        isSubmittingRef.current = isSubmitting;
        return <Form className={styles['bazaarvoice-write-review-modal']}>
              <div className={styles['bazaarvoice-write-review-modal__rating']}>
                <span className={styles['bazaarvoice-write-review-modal__headline']}>
                  {formatMessage('ratingLabel')}
                </span>
                <BazaarvoiceReviewStarsInputComponent value={values_0.rating} disabled={isSubmitting} onChange={(value_0): void => {
              void setFieldValue('rating', value_0);
              void setFieldTouched('rating', true, false);
            }} />
                {touched.rating && errors.rating && <span className={styles['bazaarvoice-write-review-modal__error']}>
                    {errors.rating}
                  </span>}
              </div>
              <span className={styles['bazaarvoice-write-review-modal__headline']}>
                {formatMessage('reviewLabel')}
              </span>

              <FormGroupComponent id="write-review" name="reviewText" type={InputType.TEXTAREA} label={formatMessage('reviewTextLabel')} value={values_0.reviewText} error={errors.reviewText} touched={touched.reviewText} required={true} isDisabled={isSubmitting} />
              <FormGroupComponent id="write-review" name="reviewTitle" type={InputType.TEXT} label={formatMessage('reviewTitleLabel')} value={values_0.reviewTitle} error={errors.reviewTitle} touched={touched.reviewTitle} required={true} isDisabled={isSubmitting} />
              <FormGroupComponent id="write-review" name="nickname" type={InputType.TEXT} label={formatMessage('nicknameLabel')} value={values_0.nickname} error={errors.nickname} touched={touched.nickname} required={true} isDisabled={isSubmitting} />
              <FormGroupComponent id="write-review" name="email" type={InputType.EMAIL} label={formatMessage('emailLabel')} value={values_0.email} error={errors.email} touched={touched.email} required={true} isDisabled={isSubmitting} />

              <div className={styles['bazaarvoice-write-review-modal__recommendation']}>
                <span className={styles['bazaarvoice-write-review-modal__headline']}>
                  {formatMessage('recommendationLabel')}
                </span>
                <div className={styles['bazaarvoice-write-review-modal__recommendation-buttons']}>
                  <ButtonComponent buttonType={values_0.recommendation === 'yes' ? ButtonType.SECONDARY : ButtonType.TERTIARY} text={formatMessage('recommendationYes')} pressed={values_0.recommendation === 'yes'} disabled={isSubmitting} onClick={(): void => {
                void setFieldValue('recommendation', 'yes');
                void setFieldTouched('recommendation', true, false);
              }} />
                  <ButtonComponent buttonType={values_0.recommendation === 'no' ? ButtonType.SECONDARY : ButtonType.TERTIARY} text={formatMessage('recommendationNo')} pressed={values_0.recommendation === 'no'} disabled={isSubmitting} onClick={(): void => {
                void setFieldValue('recommendation', 'no');
                void setFieldTouched('recommendation', true, false);
              }} />
                </div>
                {touched.recommendation && errors.recommendation && <span className={styles['bazaarvoice-write-review-modal__error']}>
                    {errors.recommendation}
                  </span>}
              </div>

              <span className={styles['bazaarvoice-write-review-modal__headline']}>
                {formatMessage('additionalInputLabel')}
              </span>

              {reviewFormFields.filter(field => field.id.toLowerCase().startsWith(EXTRA_FIELD_ID_PREFIX)).map(field_0 => {
            const fieldName = `extraFields.${field_0.id}`;
            const inputType = getExtraFieldInputType(field_0.type);
            // Formik's submit-time "touch everything" doesn't reach into this dynamically
            // built nested path, so a field the user never focused/blurred would otherwise
            // never surface its error -- fall back to "a submit was attempted".
            const fieldTouched = Boolean(getIn(touched, fieldName)) || submitCount > 0;
            return <FormGroupComponent key={field_0.id} id="write-review" name={fieldName} type={inputType} label={field_0.label} value={getIn(values_0, fieldName, '')} error={getIn(errors, fieldName)} touched={fieldTouched} required={field_0.required} isDisabled={isSubmitting} selectOptions={inputType === InputType.SELECT ? field_0.options.map(option => ({
              value: option.value,
              name: option.label
            })) : undefined} />;
          })}

              <span className={styles['bazaarvoice-write-review-modal__headline']}>
                {formatMessage('photosLabel')}
              </span>

              <FileInputComponent name="photos" files={values_0.photos} thumbnails={true} addLabel={formatMessage('addPhotoButton')} removeLabel={formatMessage('removePhotoButton')} limit={MAX_PHOTOS_TOTAL_SIZE_BYTES} accept={ACCEPTED_PHOTO_TYPES} errorMessage={formatMessage('photoTypeError')} multiple={true} disabled={isSubmitting} />
              {touched.photos && errors.photos && <span className={styles['bazaarvoice-write-review-modal__error']}>
                  {String(errors.photos)}
                </span>}

              <FormGroupComponent id="write-review" name="termsAccepted" type={InputType.CHECKBOX} label={<span>
                    {formatMessage('consentLabelPrefix')}{' '}
                    <button type="button" className={styles['bazaarvoice-write-review-modal__terms-link']} onClick={(): void => setIsTermsModalOpen(true)}>
                      {formatMessage('termsLinkText')}
                    </button>{' '}
                    {formatMessage('consentLabelSuffix')}
                  </span>} error={errors.termsAccepted} touched={touched.termsAccepted} required={true} isDisabled={isSubmitting} />
              {(status as WriteReviewModalStatus).hasError && <div className={styles['bazaarvoice-write-review-modal__submit-error']}>
                  <AlertComponent alertType={AlertType.ERROR} content={formatMessage('errors.submitFailed')} />
                </div>}
              <ButtonComponent buttonType={ButtonType.PRIMARY} text={formatMessage('submitButton')} type="submit" showSpinner={isSubmitting} disabled={isSubmitting || isLoadingExtraFormFields} />
            </Form>;
      }}
      </Formik>

      <BazaarvoiceReviewTermsModalComponent isOpen={isTermsModalOpen} onClosed={(): void => setIsTermsModalOpen(false)} />
    </PopupComponent>, document.body);
}
export default BazaarvoiceWriteReviewModalComponent;